import type { Exposure } from "@amplitude/experiment-js-client"
import type {
  DocumentStatus,
  MetadataType,
  OnboardingTrackEvent,
} from "@prismicio/editor-fields"
import { request } from "@prismicio/editor-support/Http"
import { mapValues } from "@prismicio/editor-support/Object"
import { captureException } from "@sentry/react"
import { unknown } from "zod"

import type { OnboardingStep } from "../../onboarding/OnboardingSteps"
import { getRepositoryName } from "../routing"
import { baseUrl } from "./core"

async function trackEvent(
  name: string,
  traits: Partial<Record<string, Serializable>>,
) {
  const body = {
    name,
    source: "Page Builder",
    traits: stringifyProxyTraits(traits),
    groupId: getRepositoryName(),
  }

  return request(new URL("tracking/trace", baseUrl), {
    method: "POST",
    body,
    keepalive: true,
    okType: unknown(),
  }).catch(captureException)
}

type Serializable = string | number | boolean

// Note: the traits only accept string values as our tracking proxy written in scala (/tracking/trace) is very dumb at the moment and can only parse those.
function stringifyProxyTraits(traits: Partial<Record<string, Serializable>>) {
  return mapValues(traits, (value) => value?.toString())
}

export function trackExperimentExposure(exposure: Exposure) {
  const { metadata: _, ...traits } = exposure
  void trackEvent("$exposure", traits)
}

export function trackEditorEvent(
  event: string,
  args: Record<string, string> = {},
) {
  void trackEvent(event, args)
}

export type SliceTrackingArgs = {
  id: string
  variation: string
  zone: string
  customtypeId: string
  sliceName: string
  documentName: string
  type: "shared" | "composite" | "simple"
}

type SliceAddedArgs = SliceTrackingArgs & {
  promptId?: string
}

export function trackAddSlice(args: SliceAddedArgs) {
  void trackEvent("Slice Added", args)
}

export function trackDeleteSlice(args: SliceTrackingArgs) {
  void trackEvent("Slice Deleted", args)
}

type SliceCopyPasteArgs = SliceTrackingArgs & {
  keyboard: boolean
}

export function trackCopyPasteSlice(
  action: "copy" | "paste",
  args: SliceCopyPasteArgs,
) {
  const { keyboard } = args
  const eventName = action === "copy" ? "Slice Copied" : "Slice Pasted"
  void trackEvent(eventName, { ...args, keyboard: `${keyboard}` })
}

type SliceVariationChangedArgs = SliceTrackingArgs & {
  variationName: string
}

export function trackSliceVariationChanged(args: SliceVariationChangedArgs) {
  void trackEvent("Slice Variation Changed", args)
}

export function trackSimulatorUrlSetup(args: { url: string }) {
  void trackEvent("Simulator URL Setup", args)
}

export function trackPreviewOpen(args: { documentId: string; label: string }) {
  void trackEvent("Open Preview", args)
}

export function trackSlicePromptedStarted(args: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
}) {
  void trackEvent("Slice Prompted Started", args)
}

export function trackSlicePromptedDone(args: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
  slices: string
  cost: string
  time: string
}) {
  void trackEvent("Slice Prompted Done", args)
}

export function trackRefineSliceStarted(args: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
}) {
  void trackEvent("Refine Slice Started", args)
}

export function trackRefineSliceDone(args: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
  cost: string
  time: string
}) {
  void trackEvent("Refine Slice Done", args)
}

export function trackSmartCopyPasteStarted(args: {
  customTypeId: string
  documentId?: string
  sliceId: string
  variationId: string
  retried?: "true"
}) {
  void trackEvent("Smart Copy Paste Started", args)
}

export function trackSmartCopyPasteDone(args: {
  customTypeId: string
  documentId?: string
  sliceId: string
  variationId: string
  retried?: "true"
  cost: string
  time: string
  distance: string
}) {
  void trackEvent("Smart Copy Paste Done", args)
}

export function trackTranslateDocumentStarted(args: {
  customTypeId: string
  documentId?: string
  sourceLocale: string
  targetLocale: string
  promptId: string
}) {
  void trackEvent("Translate Document Started", args)
}

export function trackTranslateDocumentDone(args: {
  customTypeId: string
  documentId?: string
  sourceLocale: string
  targetLocale: string
  promptId: string
  model: string
  sourceTokens: string
  targetTokens: string
  tokenRatio: string
  cost: string
  time: string
}) {
  void trackEvent("Translate Document Done", args)
}

export function trackDocumentCopiedToLocale(args: {
  customTypeId: string
  documentId?: string
  languageFrom: string
  languageTo: string
}) {
  void trackEvent("Document Copied To Locale", args)
}

export function trackLivePreviewModalOpen(args: {
  documentId: string
  customTypeId: string
  repositoryId: string
}) {
  void trackEvent("Live Preview Modal Open", args)
}

export function trackWhatsNewModalOpen(args: {
  documentId: string
  customTypeId: string
  repositoryId: string
}) {
  void trackEvent("Whats New Modal Open", args)
}

export function trackGlobalErrorScreenDisplayed(error: unknown) {
  const args =
    error instanceof Error
      ? { error: error.name, description: error.message }
      : { error: "Unknown", description: error?.toString() ?? "Not available" }

  void trackEvent("Global Error Screen Displayed", args)
}

export function trackDocumentWithLiveEditingPreview(args: {
  documentId: string
  customTypeId: string
  repositoryId: string
  preview: "true" | "false"
  error?: string
}) {
  void trackEvent("Document With Live Editing Preview", args)
}

type DocumentSearchInDialogArgs = {
  searchQuery: string
  numberOfWords: number
  numberOfResults: number
  timeToGetResultsInMs: number
  filtersUsed: string[]
}

export function trackDocumentSearchInDialog(args: DocumentSearchInDialogArgs) {
  void trackEvent("New Doc List Search", {
    ...args,
    filtersUsed: args.filtersUsed.join(","),
  })
}

type SelectDocumentInDialogArgs = {
  searchQuery: string
  numberOfWords: number
  numberOfResults: number
  searchPosition: number
}

export function trackSelectDocumentInDialog(args: SelectDocumentInDialogArgs) {
  void trackEvent("Page Selected", args)
}

const eventNameMap: Record<OnboardingTrackEvent["event"], string> = {
  "shared-onboarding:step-completed": "Prismic Onboarding Guide Step Completed",
  "shared-onboarding:step-opened": "Prismic Onboarding Guide Step Open",
  "shared-onboarding:completed": "Prismic Onboarding Guide Completed",
  "shared-onboarding:follow-tutorial":
    "Prismic Onboarding Guide Follow Tutorial",
}

export function trackSharedOnboardingGuide(event: OnboardingTrackEvent) {
  const eventName = eventNameMap[event.event]
  void trackEvent(eventName, event)
}

export type SeoSuggestionPayloadTracking = {
  documentId: string
  locale: string
}

export function trackSeoModalOpen(args: SeoSuggestionPayloadTracking) {
  void trackEvent("SEO Metadata Modal Open", args)
}

type SeoFieldTracking = {
  edited: boolean
  lengthChange: number
  characterOverlap: number
}

export function trackApplyMetadataSuggestion(
  args: {
    title?: SeoFieldTracking
    description?: SeoFieldTracking
  } & SeoSuggestionPayloadTracking,
) {
  const { title: titleField, description: descriptionField, ...rest } = args

  const createPayload = (
    field: SeoFieldTracking | undefined,
    fieldName: "title" | "description",
  ) => {
    if (!field) return {}
    return {
      [`${fieldName}Edited`]: field.edited,
      [`${fieldName}LengthChange`]: field.lengthChange,
      [`${fieldName}CharacterOverlap`]: field.characterOverlap,
    }
  }

  const titlePayload = createPayload(titleField, "title")
  const descriptionPayload = createPayload(descriptionField, "description")

  void trackEvent("Apply metadata suggestion", {
    ...rest,
    ...titlePayload,
    ...descriptionPayload,
  })
}

export function trackDiscardMetadataSuggestion(
  args: SeoSuggestionPayloadTracking,
) {
  void trackEvent("Discard metadata suggestion", args)
}

export function trackMetadataGeneration(
  args: {
    suggestion: string
    type: MetadataType
  } & SeoSuggestionPayloadTracking,
) {
  void trackEvent("Generate metadata", args)
}

type TeamSpaceCreatedArgs = {
  name: string
  customTypes: string[]
}

export function trackTeamSpaceCreated(args: TeamSpaceCreatedArgs) {
  void trackEvent("Team Space Created", {
    name: args.name,
    customTypes: args.customTypes.join(","),
    numberOfCustomTypes: args.customTypes.length,
  })
}

type TeamSpaceUpdatedArgs = {
  old: TeamSpaceCreatedArgs
  new: TeamSpaceCreatedArgs
}

export function trackTeamSpaceUpdated(args: TeamSpaceUpdatedArgs) {
  void trackEvent("Team Space Updated", {
    oldName: args.old.name,
    oldCustomTypes: args.old.customTypes.join(","),
    oldNumberOfCustomTypes: args.old.customTypes.length,
    name: args.new.name,
    customTypes: args.new.customTypes.join(","),
    numberOfCustomTypes: args.new.customTypes.length,
  })
}

type TeamSpaceDeletedArgs = {
  name: string
  customTypes: string[]
}

export function trackTeamSpaceDeleted(args: TeamSpaceDeletedArgs) {
  void trackEvent("Team Space Deleted", {
    name: args.name,
    customTypes: args.customTypes.join(","),
    numberOfCustomTypes: args.customTypes.length,
  })
}

export function trackPbOnboardingStep(stepName: OnboardingStep) {
  void trackEvent("Page Builder Onboarding Step Passed", { stepName })
}

type DocumentEditorMergeConflictArgs = {
  currentUserId: string
  conflictUserId?: string
  documentId: string
  currentVersionId: string
  conflictVersionId: string
}

export function trackDocumentEditorMergeConflict(
  args: DocumentEditorMergeConflictArgs,
) {
  void trackEvent("Document Editor Merge Conflict", {
    ...args,
    conflictIsSelfInflicted: String(args.currentUserId === args.conflictUserId),
  })
}

type DocumentEditorMergeConflictActionArgs = {
  userId: string
  documentId: string
  currentVersionId?: string
  conflictVersionId?: string
  action: "dismiss" | "discard" | "overwrite"
}

export function trackDocumentEditorMergeConflictUserAction(
  args: DocumentEditorMergeConflictActionArgs,
) {
  void trackEvent("Document Editor Merge Conflict User Action", args)
}

type DraftSavedArgs = {
  replacedPreviousDraft: boolean
}

export function trackDraftSaved(args: DraftSavedArgs) {
  void trackEvent("Draft saved", args)
}

type ViewHistoryArgs = {
  fromStatus: DocumentStatus
}

export function trackViewHistory(args: ViewHistoryArgs) {
  void trackEvent("View history", args)
}

type NavigatedArgs = {
  route: string
  innerWidth: number
  innerHeight: number
  screenWidth: number
  screenHeight: number
}

export function trackNavigated(args: NavigatedArgs) {
  void trackEvent("Navigated", args)
}
